/**
 * KbdExamples.tsx
 * Comprehensive showcase of HeroUI Kbd component with trading journal context
 * 
 * Demonstrates keyboard key displays, shortcuts, and combinations
 * with practical examples relevant to trading applications
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';
import { Kbd } from '@enfomentis/ui';

export const KbdExamples: React.FC = () => {
  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Kbd keys={["command"]}>K</Kbd>
            </div>
            <h1 className="text-3xl font-bold text-text-primary">Kbd Component</h1>
          </div>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Display keyboard keys and shortcuts with professional styling and accessibility features.
            Perfect for documentation, help text, and user interface guidance.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Badge color="primary" variant="flat">HeroUI Based</Badge>
            <Badge color="secondary" variant="flat">Data Display</Badge>
            <Badge color="success" variant="flat">Theme Integrated</Badge>
          </div>
        </div>

        {/* Single Keys */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Single Keys</h2>
            <p className="text-text-secondary">Individual keyboard key displays</p>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Modifier Keys</h3>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Kbd keys={["command"]}>⌘</Kbd>
                  <span className="text-text-secondary">Command</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["shift"]}>⇧</Kbd>
                  <span className="text-text-secondary">Shift</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["ctrl"]}>⌃</Kbd>
                  <span className="text-text-secondary">Control</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["option"]}>⌥</Kbd>
                  <span className="text-text-secondary">Option/Alt</span>
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Action Keys</h3>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Kbd keys={["enter"]}>↵</Kbd>
                  <span className="text-text-secondary">Enter</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["escape"]}>⎋</Kbd>
                  <span className="text-text-secondary">Escape</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["delete"]}>⌫</Kbd>
                  <span className="text-text-secondary">Delete</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["tab"]}>⇥</Kbd>
                  <span className="text-text-secondary">Tab</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["space"]}>␣</Kbd>
                  <span className="text-text-secondary">Space</span>
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Arrow Keys</h3>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Kbd keys={["up"]}>↑</Kbd>
                  <span className="text-text-secondary">Up</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["down"]}>↓</Kbd>
                  <span className="text-text-secondary">Down</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["left"]}>←</Kbd>
                  <span className="text-text-secondary">Left</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["right"]}>→</Kbd>
                  <span className="text-text-secondary">Right</span>
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Navigation Keys</h3>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Kbd keys={["home"]}>⇱</Kbd>
                  <span className="text-text-secondary">Home</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["end"]}>⇲</Kbd>
                  <span className="text-text-secondary">End</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["pageup"]}>⇞</Kbd>
                  <span className="text-text-secondary">Page Up</span>
                </div>
                <div className="flex items-center gap-2">
                  <Kbd keys={["pagedown"]}>⇟</Kbd>
                  <span className="text-text-secondary">Page Down</span>
                </div>
              </div>
            </div>

          </CardBody>
        </Card>

        {/* Key Combinations */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Key Combinations</h2>
            <p className="text-text-secondary">Multiple key shortcuts and combinations</p>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Common Application Shortcuts</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">New Document</span>
                  <Kbd keys={["command", "shift"]}>N</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Find/Search</span>
                  <Kbd keys={["command"]}>F</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Save As</span>
                  <Kbd keys={["command", "shift"]}>S</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Print</span>
                  <Kbd keys={["command"]}>P</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Preferences</span>
                  <Kbd keys={["command"]}>Comma</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Quit Application</span>
                  <Kbd keys={["command"]}>Q</Kbd>
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Trading Journal Shortcuts</h3>
              <p className="text-sm text-text-tertiary">Hypothetical keyboard shortcuts for trading journal features</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Quick Trade Entry</span>
                  <Kbd keys={["command", "shift"]}>T</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">New Session</span>
                  <Kbd keys={["command", "shift"]}>S</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">View Analytics</span>
                  <Kbd keys={["command"]}>A</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Export Data</span>
                  <Kbd keys={["command", "shift"]}>E</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Switch Account</span>
                  <Kbd keys={["command"]}>Tab</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Quick Notes</span>
                  <Kbd keys={["command", "shift"]}>N</Kbd>
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Complex Combinations</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Force Quit</span>
                  <Kbd keys={["command", "option"]}>Esc</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Screenshot Selection</span>
                  <Kbd keys={["command", "shift"]}>4</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Open Activity Monitor</span>
                  <Kbd keys={["command", "option"]}>Esc</Kbd>
                </div>
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <span className="text-text-secondary">Developer Tools</span>
                  <Kbd keys={["command", "option"]}>I</Kbd>
                </div>
              </div>
            </div>

          </CardBody>
        </Card>

        {/* Trading Context Usage */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Trading Interface Examples</h2>
            <p className="text-text-secondary">How keyboard shortcuts might be used in trading applications</p>
          </CardHeader>
          <CardBody className="space-y-6">

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Order Management</h3>
              <div className="bg-surface p-4 rounded-lg border border-border space-y-3">
                <div className="text-sm font-medium text-text-primary mb-2">Quick Order Actions:</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Buy Market</span>
                    <Kbd keys={["shift"]}>B</Kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Sell Market</span>
                    <Kbd keys={["shift"]}>S</Kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Cancel All</span>
                    <Kbd keys={["shift"]}>C</Kbd>
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Navigation Shortcuts</h3>
              <div className="bg-surface p-4 rounded-lg border border-border space-y-3">
                <div className="text-sm font-medium text-text-primary mb-2">Application Navigation:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Dashboard</span>
                    <Kbd keys={["command"]}>1</Kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Trade History</span>
                    <Kbd keys={["command"]}>2</Kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Analytics</span>
                    <Kbd keys={["command"]}>3</Kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Settings</span>
                    <Kbd keys={["command"]}>4</Kbd>
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Help Text Integration</h3>
              <div className="bg-surface p-4 rounded-lg border border-border space-y-3">
                <p className="text-sm text-text-secondary">
                  Use <Kbd keys={["command"]}>K</Kbd> to open the command palette, or press <Kbd keys={["escape"]}>Esc</Kbd> to close any modal.
                  For quick trade entry, use <Kbd keys={["command", "shift"]}>T</Kbd> to open the trade form.
                </p>
                <p className="text-sm text-text-secondary">
                  Navigate between accounts with <Kbd keys={["command"]}>Tab</Kbd> and access help anytime with <Kbd keys={["command", "shift"]}>?</Kbd>.
                </p>
              </div>
            </div>

          </CardBody>
        </Card>

        {/* Custom Styling Examples */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-text-primary">Styling Variations</h2>
            <p className="text-text-secondary">Custom styling options using classNames prop</p>
          </CardHeader>
          <CardBody className="space-y-6">

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Custom Styles</h3>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Kbd 
                    keys={["command"]}
                    classNames={{
                      base: "bg-primary-100 dark:bg-primary-900/30",
                      abbr: "text-primary-700 dark:text-primary-300"
                    }}
                  >
                    K
                  </Kbd>
                  <span className="text-text-secondary">Primary Style</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Kbd 
                    keys={["shift"]}
                    classNames={{
                      base: "bg-success-100 dark:bg-success-900/30",
                      abbr: "text-success-700 dark:text-success-300"
                    }}
                  >
                    Enter
                  </Kbd>
                  <span className="text-text-secondary">Success Style</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Kbd 
                    keys={["escape"]}
                    classNames={{
                      base: "bg-danger-100 dark:bg-danger-900/30",
                      abbr: "text-danger-700 dark:text-danger-300"
                    }}
                  >
                    Esc
                  </Kbd>
                  <span className="text-text-secondary">Danger Style</span>
                </div>
              </div>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Size Variations</h3>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Kbd 
                    keys={["command"]}
                    classNames={{
                      base: "text-xs px-1.5 py-0.5",
                      abbr: "text-xs"
                    }}
                  >
                    Small
                  </Kbd>
                  <span className="text-text-secondary">Small</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Kbd keys={["command"]}>Default</Kbd>
                  <span className="text-text-secondary">Default</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Kbd 
                    keys={["command"]}
                    classNames={{
                      base: "text-lg px-3 py-2",
                      abbr: "text-lg"
                    }}
                  >
                    Large
                  </Kbd>
                  <span className="text-text-secondary">Large</span>
                </div>
              </div>
            </div>

          </CardBody>
        </Card>

        {/* Implementation Notes */}
        <Card className="bg-surface">
          <CardBody>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Implementation Notes</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <Badge color="primary" size="sm" className="mt-0.5">Theme</Badge>
                  <p className="text-text-secondary">
                    Uses HeroUI's complete theme integration with our custom CSS properties. 
                    Colors automatically adapt to light/dark mode.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Badge color="secondary" size="sm" className="mt-0.5">Access</Badge>
                  <p className="text-text-secondary">
                    Includes proper ARIA attributes and semantic markup for screen readers 
                    and assistive technologies.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Badge color="success" size="sm" className="mt-0.5">Usage</Badge>
                  <p className="text-text-secondary">
                    Perfect for help documentation, tooltips, modal instructions, 
                    and anywhere keyboard shortcuts need to be displayed clearly.
                  </p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};